import { useState } from 'react'
import { BackButton } from '../components/BackButton'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../api'
import { useAsync } from '../lib/useAsync'
import { useToast } from '../components/Toasts'
import { ErrorState, LoadingBlock } from '../components/States'
import { PasswordOnce } from '../components/PasswordOnce'
import {
  emailedNotice, ssoNotice, SSO_PERSON, SET_PASSWORD, canSetPassword, passwordProblem, passwordSetNotice,
} from '../lib/admin-view'
import { checkDomain, tidyDomain } from '../lib/companyRules'
import { plural } from '../lib/format'
import type { AdminHistoryEntry, AdminPerson, GiveableRole, NewLogin } from '../types'

/**
 * One company: who signs in to it, who is in it, and what has been done to it.
 */
export function CompanyDetail() {
  const { slug = '' } = useParams()
  const detail = useAsync(() => api.getCompany(slug), [slug])
  const [adding, setAdding] = useState(false)
  // `value` is null through Auth0: the person was emailed a link, and there is no password to show.
  // `sso` is set when they log in with their company single sign-on, and nothing was sent at all.
  const [password, setPassword] = useState<{ email: string; value: string | null; heading: string; sso?: boolean } | null>(null)
  // Whose password super admin is typing in, and what to say once it is set (STE-1044).
  const [settingFor, setSettingFor] = useState<AdminPerson | null>(null)
  const [passwordSet, setPasswordSet] = useState<string | null>(null)
  // Above the early returns, with every other hook. Below them it ran only
  // once the company had loaded, and React blanked the page (STE-949).
  const navigate = useNavigate()

  if (detail.loading) return <LoadingBlock label="Loading company" />
  if (detail.error) return <ErrorState message={detail.error} onRetry={detail.reload} />
  if (!detail.data) return null

  const { company, domains, people, history, ssoAvailable, setPasswordAvailable } = detail.data

  return (
    <section className="screen">
      {/*
        The arrow, not a breadcrumb.

        Both did the same job in different shapes, and a screen carrying two
        ways out asks somebody to work out whether they differ. Six screens
        teach the corner; this one taught a line of text above the title.
      */}
      <BackButton to="companies" onClick={() => navigate('/companies')} />
      <header className="title-row">
        <div>
          <h1 className="page">{company.name}</h1>
          <p className="subline">
            <code className="mono">{company.slug}</code>
            {' · '}
            {plural(people.length, 'person', 'people')}
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
            Add a person
          </button>
        </div>
      </header>

      {/* On screen and nowhere else, so it takes the top of the page until it
          is dealt with rather than sitting below a table she has to scroll. */}
      {password && password.value !== null && (
        <PasswordOnce
          email={password.email}
          password={password.value}
          heading={password.heading}
          onDone={() => setPassword(null)}
        />
      )}
      {password && password.value === null && (
        <div className="banner banner--ok" role="status">
          <span>{password.sso ? ssoNotice(password.email) : emailedNotice(password.email)}</span>
          <button type="button" className="btn btn--sm" onClick={() => setPassword(null)}>OK</button>
        </div>
      )}

      {passwordSet && (
        <div className="banner banner--ok" role="status">
          <span>{passwordSet}</span>
          <button type="button" className="btn btn--sm" onClick={() => setPasswordSet(null)}>OK</button>
        </div>
      )}

      {settingFor && (
        <SetPassword
          slug={slug}
          person={settingFor}
          onClose={() => setSettingFor(null)}
          onSet={() => {
            setPasswordSet(passwordSetNotice(settingFor.name))
            setSettingFor(null)
            detail.reload()
          }}
        />
      )}

      {adding && (
        <AddPerson
          slug={slug}
          onClose={() => setAdding(false)}
          onAdded={(made) => {
            setAdding(false)
            setPassword({
              email: made.person.email,
              value: made.password ?? null,
              heading: `${made.person.name}'s password`,
              sso: made.sso === true,
            })
            detail.reload()
          }}
        />
      )}

      <Domains slug={slug} domains={domains} onChanged={detail.reload} />

      {/* Only where people sign in through Auth0. Elsewhere the choice would do nothing. */}
      {ssoAvailable && (
        <HowPeopleLogIn slug={slug} connection={company.ssoConnection ?? null} onChanged={detail.reload} />
      )}

      <h2 className="group-head">People</h2>
      {!people.length ? (
        <div className="state state--empty">
          <h2>Nobody here yet</h2>
          <p>Nobody can sign in to this company until somebody is added.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table table--people">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Can do</th>
                <th>Status</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <PersonRow
                  key={p.userId}
                  slug={slug}
                  person={p}
                  setPasswordAvailable={setPasswordAvailable}
                  onSetPassword={() => { setPasswordSet(null); setSettingFor(p) }}
                  onPassword={(value) =>
                    setPassword({ email: p.email, value, heading: `${p.name}'s new password` })
                  }
                  onChanged={detail.reload}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <History entries={history} />
    </section>
  )
}

/* ---------- sign-in domains ---------- */

function Domains({
  slug,
  domains,
  onChanged,
}: {
  slug: string
  domains: string[]
  onChanged: () => void
}) {
  const toast = useToast()
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)

  const tidy = tidyDomain(typed)
  const problem = typed ? checkDomain(tidy) : null

  async function add() {
    setBusy(true)
    try {
      await api.addDomain(slug, tidy)
      setTyped('')
      toast.ok(`${tidy} now signs in here.`)
      onChanged()
    } catch (err) {
      toast.fail((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function remove(domain: string) {
    setBusy(true)
    try {
      await api.removeDomain(slug, domain)
      toast.ok(`${domain} no longer signs in here.`)
      onChanged()
    } catch (err) {
      toast.fail((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h2 className="group-head">Sign-in domains</h2>
      <div className="card card--pad">
        <p className="form__note">
          {/* Removing one is safe, and saying so stops her hesitating over a
              button that looks as if it locks people out. */}
          Somebody typing only their email address is sent to this company. Nobody loses
          their account when a domain is removed: their login is the short name and their
          email, and that keeps working.
        </p>

        {domains.length ? (
          <ul className="chips">
            {domains.map((d) => (
              <li key={d} className="chip">
                {d}
                <button
                  type="button"
                  className="chip__x"
                  onClick={() => void remove(d)}
                  disabled={busy}
                  aria-label={`Remove ${d}`}
                  title={`Remove ${d}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="subline">None yet. People can still sign in at this company's own address.</p>
        )}

        <div className="inline-form">
          <label className="field">
            <span className="sr-only">Add a domain</span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="northwind.com"
              aria-invalid={problem ? true : undefined}
            />
          </label>
          <button
            type="button"
            className="btn"
            onClick={() => void add()}
            disabled={busy || !tidy || Boolean(problem)}
          >
            Add
          </button>
        </div>
        {problem && <p className="hint hint--bad">{problem}</p>}
      </div>
    </>
  )
}

/* ---------- how people log in (STE-1022) ---------- */

function HowPeopleLogIn({
  slug,
  connection,
  onChanged,
}: {
  slug: string
  connection: string | null
  onChanged: () => void
}) {
  const toast = useToast()
  const [way, setWay] = useState<'password' | 'sso'>(connection ? 'sso' : 'password')
  const [typed, setTyped] = useState(connection ?? '')
  const [busy, setBusy] = useState(false)

  const name = typed.trim()
  const problem = way === 'sso' && name && !/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,126}[A-Za-z0-9])?$/.test(name)
    ? 'A connection name can only use letters, numbers and dashes, and cannot start or end with a dash.'
    : null
  const wanted = way === 'sso' ? name : ''
  const unchanged = wanted === (connection ?? '')

  async function save() {
    setBusy(true)
    try {
      const now = await api.setCompanySso(slug, wanted || null)
      toast.ok(now
        ? `People here now log in with the single sign-on connection ${now}.`
        : 'People here now log in with a password.')
      onChanged()
    } catch (err) {
      toast.fail((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h2 className="group-head">How people log in</h2>
      <div className="card card--pad">
        <p className="form__note">
          With company single sign-on, people log in with their own company login and can no longer
          log in with a password. You still decide who gets in: add each person here as usual. They
          are not sent an email.
        </p>
        <div className="inline-form">
          <label className="field">
            <span className="sr-only">How people log in</span>
            <select
              className="select"
              value={way}
              disabled={busy}
              onChange={(e) => setWay(e.target.value as 'password' | 'sso')}
            >
              <option value="password">Password</option>
              <option value="sso">Company single sign-on</option>
            </select>
          </label>
          {way === 'sso' && (
            <label className="field">
              <span className="sr-only">Single sign-on connection name</span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="Connection name, for example celestiq-sso"
                aria-invalid={problem ? true : undefined}
                disabled={busy}
              />
            </label>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => void save()}
            disabled={busy || unchanged || Boolean(problem) || (way === 'sso' && !name)}
          >
            Save
          </button>
        </div>
        {problem && <p className="hint hint--bad">{problem}</p>}
      </div>
    </>
  )
}

/* ---------- one person ---------- */

function PersonRow({
  slug,
  person,
  setPasswordAvailable,
  onSetPassword,
  onPassword,
  onChanged,
}: {
  slug: string
  person: AdminPerson
  setPasswordAvailable?: boolean
  onSetPassword: () => void
  onPassword: (password: string | null) => void
  onChanged: () => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  async function run(what: () => Promise<void>) {
    setBusy(true)
    try {
      await what()
      onChanged()
    } catch (err) {
      toast.fail((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr className={person.disabled ? 'is-off' : undefined}>
      <td>{person.name}</td>
      <td>{person.email}</td>
      <td>
        {/*
          Two choices, never super admin. Somebody who could give that out
          could give it to themselves and then read every company's survey
          data, and that is a licensing obligation rather than a preference.
        */}
        <select
          className="select select--sm"
          value={person.role === 'admin' ? 'admin' : 'manager'}
          disabled={busy}
          onChange={(e) =>
            void run(async () => {
              const role = e.target.value as GiveableRole
              await api.setPersonRole(slug, person.userId, role)
              toast.ok(`${person.name} is now ${role === 'admin' ? 'an admin' : 'a manager'}.`)
            })
          }
          aria-label={`What ${person.name} can do`}
        >
          <option value="admin">Everything, including pay data</option>
          <option value="manager">Read and suggest changes</option>
        </select>
      </td>
      <td>
        {person.noLogin ? (
          <span className="pill pill--bad">No login</span>
        ) : person.disabled ? (
          <span className="pill pill--meta">Turned off</span>
        ) : person.sso ? (
          <span className="pill pill--meta">{SSO_PERSON}</span>
        ) : person.signedIn ? (
          <span className="pill pill--ok">Signed in before</span>
        ) : (
          <span className="pill pill--warn">Not signed in yet</span>
        )}
      </td>
      <td className="added">
        {person.addedAt
          ? new Date(person.addedAt).toLocaleDateString()
          : <span className="subline">not recorded</span>}
        {/* Its own line. Squeezed onto the date's it wrapped into three. */}
        {person.addedBy && <span className="subline added__by">by {person.addedBy}</span>}
      </td>
      <td>
        {confirming ? (
          <div className="actions actions--nowrap">
            <span className="subline">Give them a new password?</span>
            <button
              type="button"
              className="btn btn--sm btn--primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const made = await api.resetPassword(slug, person.userId)
                  setConfirming(false)
                  onPassword(made)
                })
              }
            >
              Yes
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setConfirming(false)}>
              No
            </button>
          </div>
        ) : (
          // Wraps onto a second line rather than pushing "Turn off" out of sight.
          <div className="actions actions--person">
            {/* Nobody at a single sign-on company has a password to give. */}
            {!person.sso && (
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() => setConfirming(true)}
              >
                New password
              </button>
            )}
            {/* Only through Auth0, and only for somebody with a password login (STE-1044). */}
            {canSetPassword(setPasswordAvailable, person) && (
              <button type="button" className="btn btn--sm" disabled={busy} onClick={onSetPassword}>
                {SET_PASSWORD}
              </button>
            )}
            <button
              type="button"
              className={person.disabled ? 'btn btn--sm' : 'btn btn--sm btn--danger'}
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.setPersonAccess(slug, person.userId, !person.disabled)
                  toast.ok(
                    person.disabled
                      ? `${person.name} can sign in again.`
                      : `${person.name} can no longer sign in.`,
                  )
                })
              }
            >
              {person.disabled ? 'Turn back on' : 'Turn off'}
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

/* ---------- setting a password (STE-1044) ---------- */

/**
 * Super admin types a password twice. It goes to the server once and is
 * forgotten here as soon as it is set: nothing on this page shows it again.
 */
function SetPassword({
  slug,
  person,
  onClose,
  onSet,
}: {
  slug: string
  person: AdminPerson
  onClose: () => void
  onSet: () => void
}) {
  const [first, setFirst] = useState('')
  const [second, setSecond] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const problem = passwordProblem(first, second)
  // Only once both are typed, so the first box is not marked wrong while she is still on it.
  const mismatch = first && second && problem ? problem : null

  async function save() {
    setBusy(true)
    setError('')
    try {
      await api.setPassword(slug, person.userId, first, second)
      onSet()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The password could not be set.')
      setBusy(false)
    }
  }

  return (
    <div className="card card--pad form" role="dialog" aria-labelledby="set-password">
      <h2 id="set-password" className="group-head group-head--flush">Set a password for {person.name}</h2>
      <p className="form__note">
        {person.email} is not sent an email, so tell them the new password yourself. They are signed
        out everywhere as soon as it is set.
      </p>

      <div className="form__row">
        <label className="field">
          <span>New password</span>
          <input type="password" autoComplete="new-password" value={first} onChange={(e) => setFirst(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Type it again</span>
          <input
            type="password"
            autoComplete="new-password"
            value={second}
            onChange={(e) => setSecond(e.target.value)}
            aria-invalid={mismatch ? true : undefined}
          />
        </label>
      </div>
      {mismatch && <p className="hint hint--bad">{mismatch}</p>}

      {error && (
        <div className="banner banner--bad" role="alert">
          <span aria-hidden="true">!</span>
          {error}
        </div>
      )}

      <div className="actions">
        <button type="button" className="btn btn--primary" disabled={Boolean(problem) || busy} onClick={() => void save()}>
          {busy ? 'Setting…' : SET_PASSWORD}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </div>
  )
}

/* ---------- adding somebody ---------- */

function AddPerson({
  slug,
  onClose,
  onAdded,
}: {
  slug: string
  onClose: () => void
  onAdded: (made: NewLogin) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<GiveableRole>('manager')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const ready = email.includes('@')

  async function add() {
    setBusy(true)
    setError('')
    try {
      const made = await api.addPerson(slug, { email: email.trim().toLowerCase(), name: name.trim(), role })
      onAdded(made)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That person could not be added.')
      setBusy(false)
    }
  }

  return (
    <div className="card card--pad form" role="dialog" aria-labelledby="add-person">
      <h2 id="add-person" className="group-head group-head--flush">Add a person</h2>

      <div className="form__row">
        <label className="field">
          <span>Their name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Their email address</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">
          <span>What they can do</span>
          <select className="select" value={role} onChange={(e) => setRole(e.target.value as GiveableRole)}>
            <option value="manager">Read and suggest changes</option>
            <option value="admin">Everything, including pay data</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="banner banner--bad" role="alert">
          <span aria-hidden="true">!</span>
          {error}
        </div>
      )}

      <div className="actions">
        <button type="button" className="btn btn--primary" disabled={!ready || busy} onClick={() => void add()}>
          {busy ? 'Adding…' : 'Add them'}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </div>
  )
}

/* ---------- what has been done ---------- */

const SAID: Record<AdminHistoryEntry['action'], (e: AdminHistoryEntry) => string> = {
  'company.created': (e) => `Created ${e.detail?.name ?? 'the company'}`,
  'domain.added': (e) => `Added the domain ${e.detail?.domain ?? ''}`,
  'domain.removed': (e) => `Removed the domain ${e.detail?.domain ?? ''}`,
  'person.added': (e) => `Added ${e.personEmail ?? 'somebody'} as ${e.detail?.role ?? 'a user'}`,
  'person.role': (e) => `Changed ${e.personEmail ?? 'somebody'} to ${e.detail?.role ?? 'another role'}`,
  'person.access': (e) => `Access ${e.detail?.access ?? 'changed'} for ${e.personEmail ?? 'somebody'}`,
  // Never what the password was, how long it was, or anything derived from it.
  'person.password': (e) => `Gave ${e.personEmail ?? 'somebody'} a new password`, // not-a-credential: an audit message, no value in it
  // Who and when only. The password is never written down.
  'person.password.set': (e) => `Set a new password for ${e.personEmail ?? 'somebody'}`, // not-a-credential: an audit message, no value in it
  'company.sso': (e) => e.detail?.to && e.detail.to !== 'none'
    ? `Set people to log in with the single sign-on connection ${e.detail.to}`
    : 'Set people to log in with a password',
}

function History({ entries }: { entries: AdminHistoryEntry[] }) {
  if (!entries.length) return null
  return (
    <>
      <h2 className="group-head">What has been done</h2>
      <div className="card card--pad">
        <ol className="history">
          {entries.map((e) => (
            <li key={e.id} className="history__item">
              <span className="history__what">{SAID[e.action]?.(e) ?? e.action}</span>
              <span className="history__who">
                {e.byName} · {new Date(e.at).toLocaleString()}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </>
  )
}
